import {Card, Link, Text} from "@nike/eds";
import {Consignment, EquipmentVehicle, Exception} from "../../graphql/graphql";
import {useQuery} from "@apollo/client";
import {
    GLOBAL_SEARCH_CONSIGNMENTS,
    GLOBAL_SEARCH_EXCEPTIONS,
    GLOBAL_SEARCH_VEHICLES
} from "../../graphql/globalsearch.graphql";
import {ReactNode, useState} from "react";
import {SimplePagination} from "../../shared/SimplePagination";

type GlobalSearchPanelProps<T> = {
    debouncedGlobalSearch: string
    currentTerritories: string[]
    customization: GlobalSearchPanelCustomization<T>
    firstPage: ResultsWithCount<T>
    offset?: number
    pageSize?: number
}

export type GlobalSearchPanelCustomization<T> = {
    getLabel: () => string
    getPlaceholder: () => string
    getQuery: () => any
    getResult: (search: any) => ResultsWithCount<T>
    getKey: (item: T) => string
    getAllResultsHref?: (searchQuery: String) => string
    getText: (item: T) => string | ReactNode
    getSmallText?: (item: T) => string | ReactNode
}

export type GetMoreFn<T> = (offset: number, limit: number) => ResultsWithCount<T>

export type ResultsWithCount<T> = {
    results?: T[]
    count?: number,
    loading?: boolean
    error?: any
}

export const CONSIGNMENT_RESULT_TRANSFORM: GlobalSearchPanelCustomization<Consignment> = {
    getLabel: () => 'Consignments',
    getPlaceholder: () => 'No consignments found.',
    getQuery: () => GLOBAL_SEARCH_CONSIGNMENTS,
    getResult: (data: any) => {
        return {
            results: data?.search?.consignments?.items || [],
            count: data?.search?.consignments?.pageInfo?.totalCount || 0
        };
    },
    getKey: (item: Consignment) => item.id,
    getText: (item: Consignment) => <Link font={'body-2'} target='_blank' href={`/consignment/${item.id}`}><b>{item.id}</b>: {item.billOfLadingNbr}</Link>,
    getSmallText: (item: Consignment) => {
        let label = [item?.carrier].filter(Boolean).join(' / ');
        return label ? `Carrier: ${label}` : '';
    }
}

export const VEHICLE_RESULT_TRANSFORM: GlobalSearchPanelCustomization<EquipmentVehicle> = {
    getLabel: () => 'Vehicles',
    getPlaceholder: () => 'No vehicles found.',
    getQuery: () => GLOBAL_SEARCH_VEHICLES,
    getResult: (data: any) => {
        return {
            results: data?.search?.equipmentVehicles?.items || [],
            count: data?.search?.equipmentVehicles?.pageInfo?.totalCount || 0
        };
    },
    getKey: (item: EquipmentVehicle) => item.id,
    getAllResultsHref: (searchQuery: String) => `/transport-vehicles?tableSearch=${searchQuery}`,
    getText: (item: EquipmentVehicle) => <Link font={'body-2'} target='_blank' href={`/transport-vehicles/${item.id}`}><b>{item.voyageNumber}</b>: {item.carrier}</Link>,
    getSmallText: (item: EquipmentVehicle) => {
        let label = [item?.originCountry, item?.dischargeCountry].filter(Boolean).join(' to ');
        return label ? `Route: ${label}` : '';
    }
}

export const EXCEPTION_RESULT_TRANSFORM: GlobalSearchPanelCustomization<Exception> = {
    getLabel: () => 'Exceptions',
    getPlaceholder: () => 'No exceptions found.',
    getQuery: () => GLOBAL_SEARCH_EXCEPTIONS,
    getResult: (data: any) => {
        return {
            results: data?.search?.exceptions?.items || [],
            count: data?.search?.exceptions?.pageInfo?.totalCount || 0
        };
    },
    getKey: (item: Exception) => item.id,
    getAllResultsHref: (searchQuery: String) => `/exceptions?tableSearch=${searchQuery}`,
    getText: (item: Exception) => <Link font={'body-2'} target='_blank' href={`/exceptions/${encodeURIComponent(item.id)}`}><b>{item.alias}</b>: {item.description}</Link>,
    getSmallText: (item: Exception) => {
        let label = [item.consignment?.id].filter(Boolean).join(' ');
        return label ? `Consignment: ${label}` : '';
    }
}

export function GlobalSearchPanel<T> ({ debouncedGlobalSearch, currentTerritories, customization, firstPage, offset = 0, pageSize = 10 }: GlobalSearchPanelProps<T>) {
    const [currentOffset, setCurrentOffset] = useState(offset);
    const skipQuery = currentOffset === 0 || !debouncedGlobalSearch || debouncedGlobalSearch.trim() === ''
    let queryResponse = useQuery(customization.getQuery(), {
        skip: skipQuery,
        variables: { customsTerritories: currentTerritories, searchTerm: debouncedGlobalSearch, offset: currentOffset, limit: pageSize }
    });
    let resultsWithCount = skipQuery
        ? firstPage
        : {...queryResponse, ...customization.getResult(queryResponse.data)};
    let {loading = false, results = [], count = 0} = resultsWithCount;

    return (
      <Card className="eds-flex--grow-1 eds-spacing--pb-48" style={{ position: 'relative' }}>
          <Text font="title-4" as="h2"  className="eds-spacing--mb-16">{customization.getLabel()}</Text>
          <div className="eds-spacing--mb-16">
              {count === 0 && !loading && <div>{customization.getPlaceholder()}</div>}
              {results.map((item: T) => {
                  let smallText = (customization.getSmallText && customization.getSmallText(item)) || '';
                  return (
                      <div key={customization.getKey(item)} className={'eds-spacing--mb-8'} style={{lineHeight: '80%'}}>
                          {customization.getText(item)}
                          {smallText && <div><small className={'eds-color--secondary'}>{smallText}</small></div>}
                      </div>
                  );
              })}
          </div>
          <SimplePagination pageSize={pageSize} currentOffset={currentOffset} totalItems={count}
                            onPageChange={(page, offset) => setCurrentOffset(offset)}
                            style={{ position: 'absolute', bottom: '0' }}/>
          {count > 0 && customization.getAllResultsHref && (
              <Link font={'body-2'} target='_blank' href={customization.getAllResultsHref(debouncedGlobalSearch)}
                    style={{ position: 'absolute', bottom: '20px', left: '24px' }}>
                  View all results
              </Link>
          )}
      </Card>
  );
}